import { useState } from 'preact/hooks';
import { Card, Empty } from '../ui';
import type { Cast, CastMember } from './CastView';
import { pathOf, type LocationMap } from './LocationsView';

/** Mirrors CharacterTimeline in @saga/spec. */
export interface CharacterTimeline {
	steps: Array<{ id: string; label: string; placements: Array<{ character: string; location: string; inferred: boolean }> }>;
}
export interface TimelineIssue {
	severity: 'error' | 'warning';
	message: string;
	id?: string;
}

const LEFT = 240;
const LANE = 150;
const COL_GAP = 20;
const HEAD = 66;
const CHIP = 20;
const CHIP_GAP = 4;
const PAD = 12;
const LABEL_LINES = 3;

/** Character colours: readable on both the light and dark panel. */
const PALETTE = ['#e05d44', '#3b82c4', '#3aa55d', '#9b59b6', '#d4a017', '#17a2a2', '#e67e22', '#c2185b', '#6d8f1f', '#5c6bc0', '#8d6e63', '#00897b'];

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Break a label into at most `max` lines of about `width` characters. */
function wrap(text: string, width: number, max: number): string[] {
	const lines: string[] = [];
	let cur = '';
	for (const w of text.split(/\s+/).filter(Boolean)) {
		if ((cur + ' ' + w).trim().length > width && cur) {
			lines.push(cur);
			cur = w;
		} else cur = (cur + ' ' + w).trim();
	}
	if (cur) lines.push(cur);
	return lines.length > max ? [...lines.slice(0, max - 1), clip(lines.slice(max - 1).join(' '), width)] : lines;
}

/**
 * Layout: each top-level location is a column, split into a lane per sub-location the timeline
 * uses (the top-level place itself gets a lane when characters are placed there directly).
 * Moments are rows, top to bottom. A character is a chip in the lane they are in, joined to their
 * chip in the next moment by a thread, so meeting up, travelling together and splitting are visible.
 */
export function timelineLayout(timeline: CharacterTimeline, map: LocationMap, cast: Cast) {
	const order = new Map(cast.characters.map((c, i) => [c.id, i]));
	const top = (id: string) => pathOf(map, id).at(-1)?.id ?? id;
	const used: string[] = [];
	for (const s of timeline.steps) for (const p of s.placements) if (!used.includes(p.location)) used.push(p.location);
	const tops: string[] = [];
	for (const l of used) if (!tops.includes(top(l))) tops.push(top(l));
	// When the story is mostly in each place: the average moment of the placements stated there.
	const when: Record<string, number[]> = {};
	timeline.steps.forEach((s, i) => s.placements.forEach((p) => !p.inferred && (when[p.location] ??= []).push(i)));
	const mean = (l: string) => (when[l]?.length ? when[l].reduce((a, b) => a + b, 0) / when[l].length : Infinity);
	const columns = tops.map((t) => {
		const lanes = used.filter((l) => top(l) === t);
		// The general place first, then its sub-locations left to right in the order the story spends time there.
		lanes.sort((a, b) => (a === t ? -1 : b === t ? 1 : mean(a) - mean(b) || used.indexOf(a) - used.indexOf(b)));
		return { id: t, lanes };
	});
	const laneX: Record<string, number> = {};
	const colBox: Record<string, { x: number; w: number }> = {};
	let x = LEFT;
	for (const c of columns) {
		colBox[c.id] = { x, w: c.lanes.length * LANE };
		c.lanes.forEach((l, i) => (laneX[l] = x + i * LANE));
		x += c.lanes.length * LANE + COL_GAP;
	}
	const rows: Array<{ y: number; h: number }> = [];
	const chips: Record<string, Array<{ step: number; x: number; y: number; inferred: boolean; location: string }>> = {};
	let y = HEAD;
	timeline.steps.forEach((s, i) => {
		const byLane: Record<string, typeof s.placements> = {};
		for (const p of [...s.placements].sort((a, b) => (order.get(a.character) ?? 99) - (order.get(b.character) ?? 99))) (byLane[p.location] ??= []).push(p);
		const most = Math.max(1, ...Object.values(byLane).map((l) => l.length));
		const h = Math.max(LABEL_LINES * 15 + PAD * 2, PAD * 2 + most * (CHIP + CHIP_GAP));
		for (const [lane, ps] of Object.entries(byLane)) ps.forEach((p, k) => (chips[p.character] ??= []).push({ step: i, x: laneX[lane] + 8, y: y + PAD + k * (CHIP + CHIP_GAP), inferred: p.inferred, location: lane }));
		rows.push({ y, h });
		y += h;
	});
	return { columns, laneX, colBox, rows, chips, w: x - COL_GAP + 20, h: y + 20 };
}

export function TimelineView(p: { timeline: CharacterTimeline; map: LocationMap; cast: Cast; issues: TimelineIssue[]; focus?: string | null; openCharacter?: (id: string) => void; openLocation?: (id: string) => void }) {
	const { timeline, map, cast } = p;
	const [sel, setSel] = useState<string | null>(p.focus && cast.characters.some((c) => c.id === p.focus) ? p.focus : null);
	if (!timeline.steps.length) return <Empty>The compile found no moments to place characters in.</Empty>;
	const L = timelineLayout(timeline, map, cast);
	const name = (id: string) => map.locations.find((l) => l.id === id)?.name ?? id;
	const color: Record<string, string> = {};
	cast.characters.forEach((c, i) => (color[c.id] = PALETTE[i % PALETTE.length]));
	const placed = cast.characters.filter((c) => L.chips[c.id]);
	const person = (id: string): CastMember | undefined => cast.characters.find((c) => c.id === id);
	const chipW = LANE - 16;
	return (
		<div>
			<Card>
				<p class="small muted">
					Moments run top to bottom. Each column is a top-level location, split into its sub-locations; a shaded band means characters are in the same general place. Faded chips are carried over from the last moment that placed them. Click a character to follow them.
				</p>
				<div class="tl-legend" role="group" aria-label="Characters">
					{placed.map((c) => (
						<button key={c.id} class={`tl-key tier-${c.tier} ${sel === c.id ? 'sel' : ''}`} onClick={() => setSel(sel === c.id ? null : c.id)} aria-pressed={sel === c.id}>
							<span class="tl-swatch" style={{ background: color[c.id] }} />
							{c.name}
						</button>
					))}
				</div>
				<div class="tl-scroll">
					<svg class="tl" width={L.w} height={L.h} viewBox={`0 0 ${L.w} ${L.h}`} role="img" aria-label="Character locations timeline">
						{L.columns.map((c) => {
							const b = L.colBox[c.id];
							return (
								<g key={c.id}>
									<text x={b.x + b.w / 2} y={18} class="tl-col-name">
										<title>{name(c.id)}</title>
										{clip(name(c.id), Math.floor(b.w / 8))}
									</text>
									<line x1={b.x + 4} x2={b.x + b.w - 4} y1={26} y2={26} class="tl-col-rule" />
									{c.lanes.map((l) => (
										<g key={l} class="tl-lane-head" onClick={() => p.openLocation?.(l)}>
											<title>{pathOf(map, l).map((x) => x.name).reverse().join(' › ')}</title>
											<text x={L.laneX[l] + LANE / 2} y={46} class={`tl-lane-name ${l === c.id ? 'general' : ''}`}>
												{l === c.id ? 'anywhere here' : clip(name(l), 20)}
											</text>
										</g>
									))}
								</g>
							);
						})}
						{L.rows.map((r, i) => {
							const s = timeline.steps[i];
							const lines = wrap(s.label || `Moment ${i + 1}`, 34, LABEL_LINES);
							const cols = new Set(s.placements.map((pl) => L.columns.find((c) => c.lanes.includes(pl.location))!.id));
							const lanes = new Set(s.placements.map((pl) => pl.location));
							return (
								<g key={s.id}>
									<line x1={8} x2={L.w - 8} y1={r.y} y2={r.y} class="tl-row-rule" />
									<text x={12} y={r.y + PAD + 10} class="tl-step">
										<title>{s.label}</title>
										<tspan class="tl-step-n">{i + 1}</tspan>
										{lines.map((ln, k) => (
											<tspan key={k} x={32} dy={k ? 15 : 0}>
												{ln}
											</tspan>
										))}
									</text>
									{[...cols].map((c) => {
										const b = L.colBox[c];
										return <rect key={c} x={b.x + 2} y={r.y + 4} width={b.w - 4} height={r.h - 8} rx={10} class="tl-band" />;
									})}
									{[...lanes].map((l) => (
										<rect key={l} x={L.laneX[l] + 4} y={r.y + 7} width={LANE - 8} height={r.h - 14} rx={7} class="tl-cell" />
									))}
								</g>
							);
						})}
						{placed.map((c) => {
							const cs = L.chips[c.id];
							const segs = cs.slice(1).map((b, k) => {
								const a = cs[k];
								if (b.step !== a.step + 1) return null;
								const ax = a.x + chipW / 2;
								const ay = a.y + CHIP;
								const bx = b.x + chipW / 2;
								const by = b.y;
								const dy = (by - ay) / 2;
								return <path key={k} d={`M ${ax} ${ay} C ${ax} ${ay + dy}, ${bx} ${by - dy}, ${bx} ${by}`} />;
							});
							return (
								<g key={c.id} class={`tl-thread tier-${c.tier} ${sel && sel !== c.id ? 'dim' : ''} ${sel === c.id ? 'on' : ''}`} stroke={color[c.id]}>
									{segs}
								</g>
							);
						})}
						{placed.map((c) =>
							L.chips[c.id].map((ch) => (
								<g key={`${c.id}-${ch.step}`} class={`tl-chip ${ch.inferred ? 'inferred' : ''} ${sel && sel !== c.id ? 'dim' : ''} ${sel === c.id ? 'on' : ''}`} transform={`translate(${ch.x}, ${ch.y})`} onClick={() => setSel(sel === c.id ? null : c.id)}>
									<title>
										{c.name} — {pathOf(map, ch.location).map((x) => x.name).reverse().join(' › ')}
										{ch.inferred ? ' (carried over)' : ''}
									</title>
									<rect width={chipW} height={CHIP} rx={CHIP / 2} style={{ stroke: color[c.id] }} />
									<circle cx={CHIP / 2} cy={CHIP / 2} r={5} style={{ fill: color[c.id] }} />
									<text x={CHIP} y={CHIP / 2 + 4}>
										{clip(c.name, 17)}
									</text>
								</g>
							)),
						)}
					</svg>
				</div>
			</Card>
			{sel && person(sel) ? (
				<Card title={`Following ${person(sel)!.name}`}>
					<ol class="list small">
						{timeline.steps.map((s, i) => {
							const at = s.placements.find((pl) => pl.character === sel);
							if (!at) return null;
							const with_ = s.placements.filter((pl) => pl.character !== sel && pl.location === at.location).map((pl) => person(pl.character)?.name ?? pl.character);
							return (
								<li key={s.id}>
									<strong>{i + 1}.</strong> {pathOf(map, at.location).map((x) => x.name).reverse().join(' › ')}
									{at.inferred ? <span class="muted"> (carried over)</span> : null}
									{with_.length ? <span class="muted"> · with {with_.join(', ')}</span> : null}
									{s.label ? <div class="muted">{s.label}</div> : null}
								</li>
							);
						})}
					</ol>
					{p.openCharacter ? (
						<button class="linkish small" onClick={() => p.openCharacter!(sel)}>
							Open {person(sel)!.name}’s character card
						</button>
					) : null}
				</Card>
			) : null}
			{p.issues.length ? (
				<Card title="To check">
					<ul class="issues">
						{p.issues.map((i, n) => (
							<li key={n} class={i.severity === 'error' ? 'bad' : 'warn'}>
								{i.message}
							</li>
						))}
					</ul>
				</Card>
			) : null}
			<Card>
				<details>
					<summary>Character locations JSON</summary>
					<pre class="mono small">{JSON.stringify(timeline, null, 2)}</pre>
				</details>
			</Card>
		</div>
	);
}
